import { LoaderLifestyleHorizontal } from '@collinsonx/design-system';
import {
  Badge,
  Flex,
  Image,
  Loader,
  Skeleton,
  Text,
  Title,
} from '@collinsonx/design-system/core';
import { getItem } from '@lib';
import { BOOKING_MODE } from 'config/constants';
import { shouldUseValuationPricing, usePerVisitLimit } from 'config/features';
import { BOOKING_MODE_STATE } from 'config/storage';
import useBooking from 'hooks/useBooking';
import useGetEntitlements from 'hooks/useGetEntitlements';
import useLocale from 'hooks/useLocale';
import useProduct from 'hooks/useProduct';
import Head from 'next/head';
import React, { memo, useMemo } from 'react';
import { optimizeContentfulImage } from 'utils/contentfulImage';
import { getCurrencySymbol } from 'utils/currencysymbol';

import classes from './LoungeInfo.module.css';

const LoungeInfo = ({
  hideImage = false,
  hideImageMobile = false,
  hidePrice = false, // temporal solution
  overrideMemberPriceFormatted,
  // Resolved below from the category-aware `shouldUseValuationPricing`
  // helper when not explicitly overridden by the caller, so every render
  // site opts into the valuation-driven display (form-page-only price +
  // "per adult" copy + Loader while fetching) when the relevant flag is
  // on for that product's category, without requiring per-call wiring.
  // When off, the legacy "per person" price row is shown on every
  // consumer page.
  useValuationOnly: useValuationOnlyProp,
  valuationLoading = false,
  width = '100%',
}: {
  hideImage?: boolean;
  hideImageMobile?: boolean;
  hidePrice?: boolean;
  overrideMemberPriceFormatted?: string | null;
  useValuationOnly?: boolean;
  valuationLoading?: boolean;
  width?: string;
} = {}) => {
  const mode = getItem(BOOKING_MODE_STATE);
  const {
    airportName,
    category,
    currency,
    imageUrl,
    loadingProduct,
    loungeName,
    saleprice,
    terminal,
  } = useProduct();

  const useValuationOnly =
    useValuationOnlyProp ?? shouldUseValuationPricing(category);
  const { booking } = useBooking();
  const {
    availableEntitlements,
    disableEntitlements,
    hasEntitlements,
    hasPermissionsForEntitlements,
    hasUnlimitedEntitlements,
    isFastTrackProduct,
    loadingEntitlements,
    remainingEntitlementsCount,
    usedEntitlementsCount,
  } = useGetEntitlements();

  const { pendingPayment, selectedEntitlementsCount } = booking;

  const loungeLocation = useMemo(() => {
    if (!airportName) return '';

    return terminal ? `${airportName}, ${terminal}` : airportName;
  }, [airportName, terminal]);

  const optimizedImageUrl = useMemo(
    () => optimizeContentfulImage(imageUrl, { width: 728 }),
    [imageUrl]
  );

  const translation = useLocale();

  const isCreateFormPage = window.location.pathname === '/';
  const isAmendFormPage = window.location.pathname === '/amend-booking';
  const isFormPage = isCreateFormPage || isAmendFormPage;

  // Valuation branch pins the price row to the get-availability form pages
  // (`/` and `/amend-booking`) because non-form pages render the total via
  // `TotalPriceBlock`. Legacy branch preserves the pre-valuation UX by
  // showing the price whenever the caller doesn't pass `hidePrice` AND
  // there is still a pending payment (or we are on a form page).
  const showPrice =
    !hidePrice &&
    (useValuationOnly ? isFormPage : pendingPayment || isFormPage);
  const showUnlimitedEntitlements =
    isFormPage && hasUnlimitedEntitlements && hasPermissionsForEntitlements;

  // Price row is composed from three signals so the intent is clear:
  //   - `formattedLegacyPrice` is the legacy product-adapter derived string.
  //   - `priceText` is the string we actually want to render.
  //   - `isPriceFetching` swaps the value for an inline Loader.
  //   - `renderPriceRow` gates the whole row.
  // Match the pre-branch behaviour: a falsy `saleprice` (including `0`)
  // hides the row entirely rather than rendering `£0.00`.
  const formattedLegacyPrice = saleprice
    ? `${getCurrencySymbol(currency ?? '')}${parseFloat(
        saleprice.toString()
      ).toFixed(2)}`
    : null;

  const priceText = useValuationOnly
    ? overrideMemberPriceFormatted ?? null
    : formattedLegacyPrice;

  const isPriceFetching = useValuationOnly && valuationLoading;
  const renderPriceRow = showPrice && (isPriceFetching || priceText != null);

  const getEntitlementsInfoState = () => {
    if (loadingEntitlements)
      return {
        showEntitlementsBadge: false,
        showEntitlementsCount: false,
        showLoadingEntitlements: true,
      };

    if (
      !hasPermissionsForEntitlements ||
      !hasEntitlements ||
      disableEntitlements
    )
      return {
        showEntitlementsBadge: false,
        showEntitlementsCount: false,
        showLoadingEntitlements: false,
      };

    if (mode === BOOKING_MODE.CREATE)
      return {
        showEntitlementsBadge: Boolean(
          !isCreateFormPage && selectedEntitlementsCount
        ),
        showEntitlementsCount: Boolean(
          isCreateFormPage &&
            remainingEntitlementsCount &&
            !showUnlimitedEntitlements
        ),
      };

    if (mode === BOOKING_MODE.EDIT)
      return {
        showEntitlementsBadge: Boolean(
          !showUnlimitedEntitlements &&
            selectedEntitlementsCount &&
            ((isAmendFormPage && !pendingPayment) || !isAmendFormPage)
        ),
        showEntitlementsCount: Boolean(
          isAmendFormPage &&
            remainingEntitlementsCount &&
            pendingPayment &&
            !showUnlimitedEntitlements
        ),
      };

    return {
      showEntitlementsBadge: Boolean(usedEntitlementsCount),
      showEntitlementsCount: false,
    };
  };

  const {
    showEntitlementsBadge,
    showEntitlementsCount,
    showLoadingEntitlements,
  } = getEntitlementsInfoState();

  return (
    <Flex
      align="center"
      bg="#FFF"
      className={classes.container}
      direction={{ base: 'column', xl: 'row' }}
      gap={16}
      justify="center"
      p={24}
      w={width}
    >
      <div>
        <Skeleton visible={loadingProduct} width="100%">
          {!hideImage && optimizedImageUrl && (
            <>
              <Head>
                <link
                  as="image"
                  href={optimizedImageUrl}
                  rel="preload"
                  {...{ fetchpriority: 'high' }}
                />
              </Head>
              <Image
                alt="lounge image"
                className={classes.image}
                h={204}
                src={optimizedImageUrl}
                visibleFrom={hideImageMobile ? 'sm' : undefined}
                w={364}
                {...{ fetchpriority: 'high' }}
              />
            </>
          )}
        </Skeleton>
      </div>
      <Flex
        className={classes.centerMobile}
        direction="column"
        gap={loadingProduct ? 16 : undefined}
        miw={200}
        w="100%"
      >
        <Skeleton visible={loadingProduct}>
          <Title
            aria-label={translation.generic.labels.loungeName}
            fw={600}
            order={2}
            size={32}
          >
            {loungeName ??
              (isFastTrackProduct ? translation.fastTrack.airport : '-')}
          </Title>
        </Skeleton>
        <Skeleton visible={loadingProduct}>
          <Text aria-label={translation.generic.labels.airport} size="lg">
            {loungeLocation}
          </Text>
        </Skeleton>
        {renderPriceRow && (
          <Skeleton visible={loadingProduct}>
            <Flex className={classes.containerPrice} gap={6}>
              {isPriceFetching ? (
                <Loader
                  aria-label={translation.generic.labels.loading}
                  size="sm"
                />
              ) : (
                <>
                  <Text
                    aria-label={
                      useValuationOnly
                        ? translation.generic.labels.pricePerAdult
                        : translation.generic.labels.pricePerPerson
                    }
                    className={classes.textPrice}
                    fw={700}
                  >
                    {priceText ?? '\u00A0'}
                  </Text>
                  <Text size="xl" style={{ lineHeight: '50px' }}>
                    {useValuationOnly
                      ? translation.lounge.perAdult
                      : translation.lounge.perPerson}
                  </Text>
                </>
              )}
            </Flex>
          </Skeleton>
        )}
        {showLoadingEntitlements && (
          <Flex justify="center">
            <LoaderLifestyleHorizontal />
          </Flex>
        )}
        {showEntitlementsBadge && (
          <Skeleton visible={loadingProduct}>
            <EntitlementUsedBadge />
          </Skeleton>
        )}
        {showUnlimitedEntitlements && !disableEntitlements && (
          <>
            <Flex align="center" justify="center">
              <Text size="lg" style={{ textAlign: 'center' }}>
                {
                  translation.booking.entitlements.loungeInfo
                    .unlimitedEntitlements
                }
              </Text>
            </Flex>
            {usePerVisitLimit && (
              <Flex align="center" className={classes.warning} justify="center">
                <Text size="sm">
                  {translation.booking.entitlements.perVisitRestrictionWarning}
                </Text>
              </Flex>
            )}
          </>
        )}
        {showEntitlementsCount && !disableEntitlements && (
          <>
            <Flex
              className={classes.entitlementsCount}
              gap={8}
              mt={10}
              justify="center"
            >
              <Text size="lg" style={{ fontWeight: 'bold' }}>
                {availableEntitlements}{' '}
              </Text>
              <Text size="lg">
                {availableEntitlements === 1
                  ? translation.booking.entitlements.loungeInfo
                      .entitlementsRemaining.singular
                  : translation.booking.entitlements.loungeInfo
                      .entitlementsRemaining.plural}
              </Text>
            </Flex>
            {usePerVisitLimit && (
              <Flex align="center" className={classes.warning} justify="center">
                <Text size="sm">
                  {translation.booking.entitlements.perVisitRestrictionWarning}
                </Text>
              </Flex>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};

const EntitlementUsedBadge = () => {
  const translation = useLocale();

  return (
    <Badge
      color="var(--mantine-color-gray-8)"
      mt={20}
      size="xl"
      style={{
        borderColor: 'var(--mantine-color-gray-2)',
        borderWidth: 2,
        fontSize: 14,
        fontWeight: 500,
        paddingX: 16,
        paddingY: 6,
        textTransform: 'none',
      }}
      variant="outline"
    >
      {translation.booking.entitlements.loungeInfo.entitlementsUsed}
    </Badge>
  );
};

export default memo(LoungeInfo);
